import { FC, useState, useMemo, useCallback } from 'react';
import { Img } from 'react-image';

import { ProductDto, PaymentType } from '@app/types/product.type';
import { useGetProductInitQuery, useGetUserInitQuery } from '@app/store/api/user.api';
import { useAppSelector } from '@app/hooks/use-store';
import { xhrHeaders } from '@lib/utils';
import { Skeleton } from '@app/components/ui';
import { ConfiguredToaster } from '@app/shared/ui/sonner';
import { useTranslation } from '@app/app/i18n';
import { analytics } from '@app/app/analytics';
import { PlatformName } from '@app/app/analytics/types';

import { CheckoutWidget } from '@app/widgets/checkout-widget';
import {
  Dialog,
  DialogContent,
  DialogClose,
  DialogTitle,
  DialogDescription,
} from '@app/shared/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { CrossAltIcon, DiamondIcon, ErrorCircleIcon } from '@app/components/ui/icons';
import { cvaIcon } from '@app/shared/ui/elements';
import { StandardTooltip } from '@app/shared/ui/standard-tooltip';
import { TooltipProvider } from '@app/shared/ui/tooltip';
import {
  mapPaymentProvidersToUrls,
  getPaymentTypeFromMethodId,
} from '@app/widgets/checkout-widget/utils/payment-provider-mapping';
import {
  calculateNextPaymentDate,
  getBillingPeriod,
} from '@app/widgets/checkout-widget/utils/date-calculations';
import { calculateSavings } from '@app/widgets/checkout-widget/utils/savings-calculation';

import { AiTokensCardCompact } from './components/ai-tokens-card-compact';
import { PlansSelectorCompact } from './components/plans-selector-compact';
import { UpgradePopupProps, PurchaseType } from './types';
import { UPGRADE_POPUP_CONFIGS } from './config/upgrade-popup-config';

export const UpgradePopup: FC<UpgradePopupProps> = ({
  variant,
  popupName,
  contextMessageKey,
  isOpen,
  onClose,
  onSelectProduct,
  currentTokensCount,
  userHasSubscription = false,
  onClickDownload,
  onShareByLink,
}) => {
  const { t } = useTranslation('trial-popup');
  const { data: productResponse, isLoading: isLoadingProducts } = useGetProductInitQuery(null, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const { isAuthenticated } = useAppSelector((state) => state.authState);
  const { data: userData } = useGetUserInitQuery(null, { skip: !isAuthenticated });

  // Вычисляем текущее количество токенов из userData
  const actualTokensCount = useMemo(() => {
    if (!userData?.result?.product_balance) return currentTokensCount;
    const { included_ai_token_count = 0, purchased_ai_token_count = 0 } =
      userData.result.product_balance;
    return included_ai_token_count + purchased_ai_token_count;
  }, [userData, currentTokensCount]);

  const [showCheckoutWidget, setShowCheckoutWidget] = useState(false);
  const [chosenTariff, setChosenTariff] = useState<ProductDto & { productTitle: string }>();
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Определяем конфигурацию для текущего варианта
  const config = useMemo(() => {
    // Для ai_tokens варианта выбираем подвариант в зависимости от наличия подписки
    if (variant === 'ai_tokens_trial' || variant === 'ai_tokens_subscription') {
      return userHasSubscription
        ? UPGRADE_POPUP_CONFIGS.ai_tokens_subscription
        : UPGRADE_POPUP_CONFIGS.ai_tokens_trial;
    }
    return UPGRADE_POPUP_CONFIGS[variant] || UPGRADE_POPUP_CONFIGS.fallback;
  }, [variant, userHasSubscription]);

  // Группируем продукты
  const products = useMemo(() => {
    if (!productResponse?.result) return null;

    const allProducts = productResponse.result.products;

    const groupedProducts: {
      starter?: { monthly?: ProductDto; yearly?: ProductDto };
      basic?: { monthly?: ProductDto; yearly?: ProductDto };
      expert?: { monthly?: ProductDto; yearly?: ProductDto };
    } = {};

    allProducts.forEach((product) => {
      if (product.type !== 'subscription') return;

      const planName = product.name.toLowerCase();
      const isMonthly = product.duration.months === 1;
      const isYearly = product.duration.years === 1;

      if (planName === 'starter') {
        if (!groupedProducts.starter) groupedProducts.starter = {};
        if (isMonthly) groupedProducts.starter.monthly = product;
        if (isYearly) groupedProducts.starter.yearly = product;
      } else if (planName === 'basic') {
        if (!groupedProducts.basic) groupedProducts.basic = {};
        if (isMonthly) groupedProducts.basic.monthly = product;
        if (isYearly) groupedProducts.basic.yearly = product;
      } else if (planName === 'expert') {
        if (!groupedProducts.expert) groupedProducts.expert = {};
        if (isMonthly) groupedProducts.expert.monthly = product;
        if (isYearly) groupedProducts.expert.yearly = product;
      }
    });

    return {
      plans: groupedProducts,
      aiTokens: allProducts.find((p) => p.type === 'ai_token'),
    };
  }, [productResponse]);

  const buyTariff = useCallback(
    async (
      paymentType: PaymentType,
      tariff: (ProductDto & { productTitle: string }) | undefined = chosenTariff,
    ) => {
      if (!tariff) return;

      if (popupName) {
        analytics.emitEvent('trial_popup_to_checkout', {
          [PlatformName.GTM]: { popup_name: popupName },
        });
      }

      const urlToPay = tariff.payment_provider.find((item) => item.type === paymentType)?.url;
      if (!urlToPay) return;

      if (userData?.result?.product_balance) {
        window.sessionStorage.setItem('gotoProduct', JSON.stringify({ url: urlToPay }));
      }

      // Отправляем begin_checkout перед переходом в платежную систему
      const price = parseFloat(tariff.price.amount);
      const productType: 'subscription' | 'pay_as_you_go' | 'ai_tokens' =
        tariff.type === 'ai_token' ? 'ai_tokens' : tariff.count ? 'pay_as_you_go' : 'subscription';
      const itemVariant =
        productType === 'ai_tokens'
          ? 'ai_tokens'
          : tariff.duration.years === 1
            ? 'yearly_plan'
            : productType === 'pay_as_you_go'
              ? 'payg_10'
              : 'monthly_plan';

      analytics.emitEvent('begin_checkout', {
        [PlatformName.GTM]: {
          ecommerce: {
            currency: tariff.price.currency,
            value: price,
            payment_method: paymentType,
            product_id: tariff.id,
            items: [
              {
                item_name: productType,
                item_variant: itemVariant,
                price,
                quantity: 1,
              },
            ],
          },
        },
      });

      async function handlePostRequest(url: string) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              ...xhrHeaders(),
            },
            redirect: 'manual',
          });
          const res = await response.json();

          let redirectUrl = res.result;

          const currentPath = window.location.pathname;
          const workspacePattern = /^\/workspace\/\d+\//;

          if (workspacePattern.test(currentPath)) {
            const url = new URL(redirectUrl, window.location.origin);
            url.searchParams.set('next', currentPath);
            redirectUrl = url.pathname + url.search;
          }

          history.pushState(null, '', window.location.pathname);
          window.location.replace(redirectUrl);
        } catch (e) {
          window.location.href = `/login?next=${window.location.pathname}`;
          console.error(e, 'error in handlePostRequest');
        }
      }
      await handlePostRequest(urlToPay as string);
    },
    [chosenTariff, userData, popupName],
  );

  // Обработчик выбора продукта
  const handleSelectProduct = useCallback(
    (product: ProductDto) => {
      if (onSelectProduct) {
        onSelectProduct(product);
        return;
      }

      const paymentProviders = product.payment_provider || [];

      // Для AI токенов - прямой редирект без виджета
      if (product.type === 'ai_token') {
        // Проверяем наличие подписки
        if (!userHasSubscription) {
          ConfiguredToaster.toastWarning(t('aiTokensRequireSubscription'), {
            duration: 5000,
          });
          return;
        }

        if (paymentProviders.length === 1) {
          buyTariff(paymentProviders[0].type, { ...product, productTitle: product.name });
          return;
        }
      }

      // Для подписок и Pay-As-You-Go - прямой редирект (CheckoutWidget временно отключен)
      if (product.type === 'subscription' || product.type === 'presentation') {
        if (paymentProviders.length > 0) {
          buyTariff(paymentProviders[0].type, { ...product, productTitle: product.name });
        }
        return;
      }

      // Fallback для других типов
      if (paymentProviders.length === 1) {
        buyTariff(paymentProviders[0].type, { ...product, productTitle: product.name });
        return;
      }
    },
    [onSelectProduct, userHasSubscription, t, buyTariff],
  );

  // Обработчик выбора метода оплаты в CheckoutWidget
  const handleCheckoutPaymentMethodSelect = useMemo(
    () => async (methodId: string) => {
      if (!chosenTariff) return;

      setIsProcessingPayment(true);

      try {
        const paymentType = getPaymentTypeFromMethodId(methodId);
        if (!paymentType) {
          console.error('Unknown payment method:', methodId);
          setIsProcessingPayment(false);
          return;
        }

        await buyTariff(paymentType, chosenTariff);
      } catch (error) {
        console.error('Error processing payment:', error);
        setIsProcessingPayment(false);
      }
    },
    [chosenTariff, buyTariff],
  );

  // Вычисление пропсов для CheckoutWidget
  const checkoutWidgetProps = useMemo(() => {
    if (!chosenTariff) return null;

    const paymentUrls = mapPaymentProvidersToUrls(chosenTariff);
    const billingPeriod = getBillingPeriod(chosenTariff);
    const nextPaymentDate = calculateNextPaymentDate(chosenTariff);

    // Вычисляем экономию для yearly планов
    let savings: number | undefined;
    if (billingPeriod === 'yearly' && products) {
      const planName = chosenTariff.name.toLowerCase();
      const monthlyProduct = products.plans?.[planName as 'starter' | 'basic' | 'expert']?.monthly;
      savings = calculateSavings(chosenTariff, monthlyProduct);
    }

    // Определяем тип продукта для аналитики
    const productType =
      chosenTariff.type === 'ai_token'
        ? 'ai_tokens'
        : chosenTariff.count
          ? 'pay_as_you_go'
          : 'subscription';

    return {
      planName: chosenTariff.name,
      aiTokens: chosenTariff.included_ai_token_count || 0,
      price: parseFloat(chosenTariff.price.amount),
      billingPeriod,
      nextPaymentDate,
      onBackToPricing: () => {
        setShowCheckoutWidget(false);
        setChosenTariff(undefined);
      },
      ...paymentUrls,
      theme: 'dark' as const,
      savings,
      onPaymentMethodSelect: handleCheckoutPaymentMethodSelect,
      isLoading: isProcessingPayment,
      productId: chosenTariff.id,
      currency: chosenTariff.price.currency,
      productType: productType as 'subscription' | 'pay_as_you_go' | 'ai_tokens',
      downloadsCount: chosenTariff.count,
    };
  }, [chosenTariff, products, isProcessingPayment, handleCheckoutPaymentMethodSelect]);

  const purchaseType: PurchaseType = config.purchaseType;

  const handleClose = useCallback(() => {
    if (popupName) {
      analytics.emitEvent('trial_popup_close', {
        [PlatformName.GTM]: { popup_name: popupName },
      });
    }
    onClose();
  }, [popupName, onClose]);

  return (
    <>
      {/* Upgrade Popup Dialog */}
      <Dialog
        open={isOpen && !showCheckoutWidget}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
      >
        <DialogContent
          className="w-fit min-w-[980px] max-h-[90vh] overflow-y-auto overflow-x-hidden p-0 !z-[150]"
          borderRadius="2xl"
          customCloseIcon={
            <DialogClose
              aria-label="Close"
              onClick={handleClose}
              className="absolute right-5 top-5 transition-colors disabled:pointer-events-none text-general_text_MII_default hover:text-general_text_MII_secondary_i focus-visible:outline z-10"
            >
              <CrossAltIcon className={cvaIcon({ size: 'small' })} />
              <span className="sr-only">Close</span>
            </DialogClose>
          }
        >
          <DialogTitle>
            <VisuallyHidden>{t(config.titleKey)}</VisuallyHidden>
          </DialogTitle>
          <DialogDescription>
            <VisuallyHidden>{t(config.descriptionKey)}</VisuallyHidden>
          </DialogDescription>

          <div className="bg-general_background_MIII_400_dark rounded-2xl">
            <div className="w-[980px] flex gap-0">
              {/* Левая часть - контент */}
              <div className="p-8 pr-[24px] flex flex-col bg-general_background_MIII_400_dark flex-1">
                <div className="p-6 flex flex-col gap-3 rounded-lg bg-general_background_MIII_450_dark grow">
                  {config.image && (
                    <div className="w-full">
                      <Img
                        src={config.image}
                        alt=""
                        loader={
                          <Skeleton
                            className="w-full rounded-lg"
                            height={config.variant === 'ai_tokens_subscription' ? '170px' : '120px'}
                          />
                        }
                        unloader={
                          <div
                            className="w-full bg-general_background_MIII_400_dark rounded-lg flex items-center justify-center"
                            style={{
                              width: '422px',
                              height:
                                config.variant === 'ai_tokens_subscription' ? '170px' : '120px',
                            }}
                          >
                            <span className="text-general_text_MII_secondary_i text-sm">
                              Image not found
                            </span>
                          </div>
                        }
                        className="w-full object-cover"
                        style={{
                          width: '422px',
                          height: config.variant === 'ai_tokens_subscription' ? '170px' : '120px',
                          borderRadius: '8px',
                        }}
                      />
                    </div>
                  )}

                  {config.showTokensCount && actualTokensCount !== undefined && (
                    <div className="flex items-center gap-2">
                      <div className="inline-flex items-center gap-1.5 py-2 px-3 rounded-3xl bg-general_background_MIII_400_dark">
                        <span className="font-inter text-xs leading-[10px] font-normal text-secondary_error_SI_400_error">
                          AI tokens
                        </span>
                        <DiamondIcon className="w-5 h-5 text-secondary_error_SI_400_error" />
                        <span className="font-inter text-xs leading-[10px] font-normal text-secondary_error_SI_400_error">
                          {actualTokensCount}
                        </span>
                      </div>
                      <TooltipProvider>
                        <StandardTooltip
                          text={t('trialPopup.upgradePopup.currentBalance', {
                            count: actualTokensCount,
                          })}
                          tooltipProps={{ delayDuration: 0 }}
                          contentProps={{ side: 'bottom' }}
                          contentClassName="bg-general_background_MIII_450_dark text-general_text_MII_secondary_i border-0"
                          arrowClassName="hidden"
                        >
                          <div className="cursor-help">
                            <ErrorCircleIcon className={cvaIcon()} />
                          </div>
                        </StandardTooltip>
                      </TooltipProvider>
                    </div>
                  )}

                  <h2 className="font-inter font-semibold text-[32px] leading-[38px] tracking-[0px] text-general_text_MII_default">
                    {t(config.titleKey)}
                  </h2>

                  <p className="font-inter font-normal text-[18px] leading-[22px] tracking-[0px] text-general_text_MII_secondary_iii">
                    {t(config.descriptionKey)}
                  </p>

                  {contextMessageKey && (
                    <p className="font-inter font-normal text-[16px] leading-[22px] tracking-[0px] text-general_text_MII_default">
                      {t(contextMessageKey)}
                    </p>
                  )}

                  {config.features.length > 0 && (
                    <ul className="space-y-2 flex-1">
                      {config.features.map((featureKey, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <span className="text-main_green w-4 h-4 flex items-center justify-center text-base font-bold">
                            ✓
                          </span>
                          <span className="font-inter font-normal text-[16px] leading-[19px] tracking-[0px] text-general_text_MII_default">
                            {t(featureKey)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Правая часть - выбор плана/токенов */}
              <div className="p-8 pl-[24px] bg-general_background_MIII_300_dark border-l border-general_background_MIII_500_dark flex-1">
                {purchaseType === 'subscription' ? (
                  <>
                    <PlansSelectorCompact
                      products={products?.plans || {}}
                      onSelectPlan={handleSelectProduct}
                      isLoading={isLoadingProducts}
                      footerSlot={
                        config.showNotReadyText && (
                          <p
                            className="font-inter font-normal text-[16px] leading-[24px] tracking-[0px] mt-2 mb-2 text-general_text_MII_secondary_i"
                            dangerouslySetInnerHTML={{
                              __html: t('trialPopup.upgradePopup.downloadTrial.notReadyYet'),
                            }}
                            onClick={(e) => {
                              const target = e.target as HTMLElement;
                              const anchor = target.closest('a');
                              if (anchor && e.currentTarget.contains(anchor)) {
                                e.preventDefault();
                                const anchors = Array.from(e.currentTarget.querySelectorAll('a'));
                                const index = anchors.indexOf(anchor);

                                if (index === 0) {
                                  if (onShareByLink) onShareByLink();
                                } else if (index === 1) {
                                  if (onClickDownload) onClickDownload();
                                }
                              }
                            }}
                          />
                        )
                      }
                    />
                  </>
                ) : (
                  <AiTokensCardCompact
                    product={products?.aiTokens}
                    onSelectProduct={handleSelectProduct}
                    isLoading={isLoadingProducts}
                  />
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout Widget Dialog */}
      <Dialog
        open={showCheckoutWidget}
        onOpenChange={(open) => {
          setShowCheckoutWidget(open);
          if (!open) {
            setChosenTariff(undefined);
          }
        }}
      >
        {checkoutWidgetProps && (
          <DialogContent
            className="max-w-7xl w-[95%] max-h-[90vh] overflow-y-auto p-0 !z-[10000]"
            borderRadius="2xl"
            customCloseIcon={
              <DialogClose
                aria-label="Close"
                className="absolute right-5 top-5 transition-colors disabled:pointer-events-none text-general_text_MII_default hover:text-general_text_MII_secondary_i focus-visible:outline z-10"
              >
                <CrossAltIcon className={cvaIcon({ size: 'small' })} />
                <span className="sr-only">Close</span>
              </DialogClose>
            }
          >
            <DialogTitle>
              <VisuallyHidden>Checkout</VisuallyHidden>
            </DialogTitle>
            <DialogDescription>
              <VisuallyHidden>Complete your purchase</VisuallyHidden>
            </DialogDescription>
            <CheckoutWidget {...checkoutWidgetProps} />
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};
